import styled from 'styled-components'
import { motion } from 'framer-motion'
import { Stack } from '@mui/material'
import { theme } from '../../theme/theme'
import { ComponentLayout } from '../layouts/ComponentLayout'
import { Arrow } from '../icons'
import { OnClickButton } from '../buttons/onclick/OnClickButton'
import { isTabletOrMobile } from '../../utils/devices'
import { useWindowWidth } from '../../hooks/useWindowWidth'
import { QuestionSkeleton } from '../QuestionSkeleton'
import { ScreeningHeader } from './ScreeningHeader'
import { ScreeningQuestion } from './ScreeningQuestion'
import { ErrorModal } from '../modals/ErrorModal'
import { useTranslation } from 'react-i18next'
import { useSubmitOnEnterKey } from '../../hooks/useSubmitOnEnterKey'
import { useScreeningQuestionnaire } from './useScreeningQuestionnaire'

type Props = {
  screeningId: string | undefined
  submitPath: string
  closePath: string
  parentScreeningDate?: string | null
}

export const ScreeningQuestionnaire = ({ screeningId, submitPath, closePath, parentScreeningDate = null }: Props) => {
  const { t } = useTranslation()
  const width = useWindowWidth()

  const {
    currentQuestion,
    isLoading,
    showSkeleton,
    isAnswerValid,
    showValidation,
    canGoBack,
    errorMessage,
    dismissError,
    goToNextQuestion,
    goToPreviousQuestion,
  } = useScreeningQuestionnaire(screeningId, submitPath)

  const isInfoQuestion = currentQuestion?.type === 'info'
  const canSubmitWithEnterKey = isAnswerValid && !isLoading && currentQuestion?.type !== 'textInputMultipleLine'

  useSubmitOnEnterKey(goToNextQuestion, canSubmitWithEnterKey)

  if (!currentQuestion) return null

  return (
    <>
      <ScreeningHeader
        title={currentQuestion.sectionTitle}
        goBack={goToPreviousQuestion}
        disableBack={!canGoBack}
        progress={currentQuestion.progress}
        date={parentScreeningDate}
        closePath={closePath}
      />
      <ComponentLayout
        noMaxWidth
        paddingTop={isTabletOrMobile(width) ? theme.spacing.xsmall : theme.spacing.xxlarge}
        style={{
          height: '100%',
        }}
      >
        <Section $isInfoType={isInfoQuestion}>
          {showSkeleton ? (
            <QuestionSkeleton />
          ) : (
            <FadeIn
              $isInfoType={isInfoQuestion}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <ScreeningQuestion {...currentQuestion} showValidation={showValidation} />
              <ButtonContainer $isInfoType={isInfoQuestion}>
                <OnClickButton
                  text={
                    isInfoQuestion ? t('employee.screening.continueButton') : t('employee.screening.nextQuestionButton')
                  }
                  onClick={goToNextQuestion}
                  icon={<Arrow color={theme.color.white} size={15} />}
                  disabled={!isAnswerValid || isLoading}
                  loading={isLoading}
                />
              </ButtonContainer>
            </FadeIn>
          )}
        </Section>
        {errorMessage && (
          <ErrorModal title={t('employee.screening.errorTitle')} text={errorMessage} onClose={dismissError} />
        )}
      </ComponentLayout>
    </>
  )
}

const Section = styled(Stack)<{ $isInfoType: boolean }>`
  max-width: ${({ $isInfoType }) => ($isInfoType ? '700px' : '600px')};
  margin: 0 auto;
  text-align: center;
  height: 100%;
`

const ButtonContainer = styled.div<{ $isInfoType: boolean }>`
  & > button {
    width: 100%;
  }

  @media screen and (min-width: ${theme.breakpoint.medium}px) {
    margin-top: 10%;
    padding-bottom: ${({ $isInfoType }) => ($isInfoType ? `${theme.spacing.medium}px` : '50%')};
    margin-left: auto;
  }
  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    padding-bottom: ${theme.spacing.medium}px;
  }
`

const FadeIn = styled(motion.create(Stack))<{ $isInfoType: boolean }>`
  height: 100%;
  justify-content: ${({ $isInfoType }) => ($isInfoType ? 'flex-start' : 'space-between')};
  gap: ${({ $isInfoType }) => ($isInfoType ? `${theme.spacing.xlarge}px` : '0')};

  @media screen and (max-width: ${theme.breakpoint.medium}px) {
    justify-content: space-between;
  }
`
